import React from 'react';
import { RED, INK, TEAL, GREY, LIGHT, SOFT, BORDER, FONT, wrap, btnRed, Hero, Crumbs, CtaBand } from '../seo/SeoUI';
import { PILLAR_SERVICES, PILLAR_CITIES, servicePath } from '../../core/seo/pillarCities';
import { cityContentFor } from '../seoLanding/cityContent';

// City "Home Services" pillar — targets "home services <city>" and links down to that city's service
// pages. Content is filled from cityContent (real neighborhoods/context/FAQ) so each pillar is unique.
function HomeServicesPillar({ city }) {
  const place = `${city.name}, ${city.state}`;
  const cc = cityContentFor(city.slug) || {};
  const hoods = cc.neighborhoods || [];
  const zips = cc.zips || [];
  const others = PILLAR_CITIES.filter((c) => c.slug !== city.slug);

  const faqs = (cc.faqs
    ? cc.faqs.map(([q, a]) => ({ q: q.replace(/\{service\}/g, 'home services'), a: a.replace(/\{service\}/g, 'home services') }))
    : [
        { q: `What home services can I book in ${city.name}?`, a: `BestHand connects you with local pros in ${city.name} for handyman work, cleaning, plumbing, electrical, painting, HVAC, moving, furniture assembly, TV mounting, yard work and more — all with upfront pricing and no lead fees.` },
        { q: `Are ${city.name} pros background-checked?`, a: `Yes. Every pro on BestHand is ID-verified and background-checked before they can take jobs in ${city.name}.` },
      ]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.besthandapp.com/' },
          { '@type': 'ListItem', position: 2, name: 'Home Services', item: 'https://www.besthandapp.com/services' },
          { '@type': 'ListItem', position: 3, name: place },
        ],
      },
      {
        '@type': 'ItemList',
        name: `Home services in ${place}`,
        itemListElement: PILLAR_SERVICES.map((s, i) => ({
          '@type': 'ListItem', position: i + 1, name: `${s.label} in ${city.name}`,
          url: `https://www.besthandapp.com${servicePath(s.slug, city)}`,
        })),
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
      },
    ],
  };

  const card = { display: 'block', background: '#fff', borderRadius: 12, padding: '16px 18px', textDecoration: 'none', color: INK, border: `1px solid ${BORDER}` };

  return (
    <div style={{ fontFamily: FONT, color: INK, background: '#fff' }}>
      {/* eslint-disable-next-line react/no-danger */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <Hero
        breadcrumb={<Crumbs items={[{ label: 'Home', href: '/' }, { label: 'Home Services', href: '/services' }, { label: city.name }]} />}
        eyebrow={`${place} · Local pros`}
        title={`Home Services in ${city.name}`}
        subtitle={`Book trusted, background-checked local pros in ${city.name} for handyman work, cleaning, plumbing, electrical, moving and more — upfront pricing, no lead fees, secure in-app payment.`}
        badges={['Background-checked pros', 'Upfront pricing', 'No lead fees']}
      >
        <a href="/category?type=all" style={btnRed}>Get a free estimate</a>
      </Hero>

      {/* Local intro */}
      <section style={{ ...wrap, padding: '40px 20px 8px' }}>
        <p style={{ fontSize: 17, color: '#374151', lineHeight: 1.75, maxWidth: 860, margin: 0 }}>
          Whatever your to-do list in {city.name}, BestHand matches you with a vetted local pro who can get to you fast.
          {hoods.length ? ` We serve ${hoods.slice(0, 6).join(', ')} and neighborhoods across ${city.name}` : ` We serve ${city.name} and the surrounding area`}
          {zips.length ? `, including ZIP codes ${zips.slice(0, 6).join(', ')}.` : '.'}
          {' '}See an upfront price for your specific job before you book — no hourly surprises and no lead fees.
        </p>
      </section>

      {/* Service grid — the maillage down to city×service pages */}
      <section style={{ ...wrap, padding: '22px 20px 8px' }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 6px' }}>Popular home services in {city.name}</h2>
        <p style={{ fontSize: 16, color: GREY, margin: '0 0 22px' }}>Pick a service to see local pros and book with upfront pricing.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {PILLAR_SERVICES.map((s) => (
            <a key={s.slug} href={servicePath(s.slug, city)} style={card}>
              <span style={{ fontWeight: 700 }}>{s.label}</span>
              <span style={{ display: 'block', fontSize: 13, color: GREY, marginTop: 3 }}>{s.label} in {city.name}, {city.state}</span>
            </a>
          ))}
        </div>
      </section>

      {/* Neighborhoods */}
      {hoods.length ? (
        <section style={{ ...wrap, padding: '30px 20px 8px' }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 12px' }}>Neighborhoods we serve in {city.name}</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {hoods.map((n) => (
              <span key={n} style={{ fontSize: 14, fontWeight: 600, color: INK, background: LIGHT, border: `1px solid ${BORDER}`, borderRadius: 999, padding: '6px 14px' }}>{n}</span>
            ))}
          </div>
        </section>
      ) : null}

      {/* How it works */}
      <section style={{ background: LIGHT, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, marginTop: 34 }}>
        <div style={{ ...wrap, padding: '40px 20px' }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 20px' }}>How BestHand works in {city.name}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {[
              ['1. Tell us the job', `Describe your ${city.name} job and pick a time that works for you.`],
              ['2. Get an upfront price', 'See a clear price before you book — no hourly surprises, no lead fees.'],
              ['3. Book a vetted pro', 'A background-checked local pro is matched to you. Pay securely in the app.'],
            ].map(([t, d]) => (
              <div key={t} style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, padding: '18px 20px' }}>
                <div style={{ fontWeight: 800, color: RED, marginBottom: 6 }}>{t}</div>
                <div style={{ fontSize: 15, color: GREY, lineHeight: 1.6 }}>{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ ...wrap, padding: '40px 20px 8px' }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 16px' }}>Home services in {city.name} — FAQ</h2>
        {faqs.map((f) => (
          <div key={f.q} style={{ borderTop: `1px solid ${BORDER}`, padding: '16px 0' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{f.q}</div>
            <div style={{ fontSize: 15, color: GREY, lineHeight: 1.7, maxWidth: 860 }}>{f.a}</div>
          </div>
        ))}
      </section>

      {/* Nearby city pillars — maillage across the zone */}
      <section style={{ ...wrap, padding: '30px 20px 8px' }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 12px' }}>Home services in nearby cities</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {others.map((c) => (
            <a key={c.slug} href={`/home-services/${c.slug}`} style={{ fontSize: 14, fontWeight: 600, color: TEAL, background: SOFT, border: `1px solid ${BORDER}`, borderRadius: 999, padding: '7px 15px', textDecoration: 'none' }}>
              {c.name}, {c.state}
            </a>
          ))}
        </div>
      </section>

      <div style={{ height: 42 }} />
      <CtaBand title={`Ready to book a pro in ${city.name}?`} subtitle="Get matched with a trusted local pro in minutes." ctaText="Get started" ctaHref="/category?type=all" />
    </div>
  );
}

export default HomeServicesPillar;
